// fuzzy.js — the pure, dependency-free fuzzy matcher for the Command Palette.
// WL (Fable Hail Mary Wave 5 — Command Palette delight item).
//
// WHY A HAND-ROLLED MATCHER (not a new dependency)
// A command palette needs subsequence matching with sensible ranking (word-start
// and prefix bonuses) plus the matched-character indices so the UI can bold the
// letters the user typed. The repo carries no such utility and adding a fuzzy lib
// (fuse.js/cmdk) would be a new heavy dependency for ~40 lines of logic. This is
// deliberately small, allocation-light, and fully unit-tested (fuzzy.test.js).
//
// CONTRACT
//   fuzzyMatch(query, text) -> { score, indices } | null
//     • null  when `text` does not contain `query` as a subsequence (no match).
//     • score higher = better. Consecutive runs, word-starts and a leading prefix
//       score higher, and shorter targets tie-break above longer ones.
//     • indices = the positions in `text` that matched, in order (for highlight).
//   An empty query matches everything with score 0 and no indices (callers show
//   the recents/empty state instead of calling this).

const isWordStart = (text, i) => {
  if (i === 0) return true;
  const prev = text[i - 1];
  // boundary after space / separators, or a camelCase hump
  return (
    prev === " " ||
    prev === "-" ||
    prev === "_" ||
    prev === "/" ||
    prev === "." ||
    (prev === prev.toLowerCase() && text[i] === text[i].toUpperCase())
  );
};

/**
 * Score a query against a target string.
 * @param {string} query raw user input
 * @param {string} text  target label / keyword
 * @returns {{score:number, indices:number[]}|null}
 */
export function fuzzyMatch(query, text) {
  if (query == null || query === "") return { score: 0, indices: [] };
  if (!text) return null;

  const q = String(query).toLowerCase().trim();
  if (q === "") return { score: 0, indices: [] };
  const t = String(text);
  const tl = t.toLowerCase();

  // Fast path: contiguous substring (best kind of match).
  const sub = tl.indexOf(q);
  const indices = [];
  let score = 0;

  let qi = 0;
  let prevMatch = -2;
  for (let ti = 0; ti < tl.length && qi < q.length; ti++) {
    if (tl[ti] === q[qi]) {
      indices.push(ti);
      let charScore = 1;
      if (ti === prevMatch + 1) charScore += 4; // consecutive run
      if (isWordStart(t, ti)) charScore += 6; // matched a word boundary
      if (ti === 0) charScore += 4; // leading prefix
      score += charScore;
      prevMatch = ti;
      qi++;
    }
  }

  if (qi < q.length) return null; // not a subsequence → no match

  // Whole-word / substring bonuses.
  if (sub === 0) score += 20; // target starts with the query
  else if (sub > 0) score += 10; // query appears contiguously somewhere

  // Prefer shorter targets on ties (a 4-char exact beats a 40-char loose match).
  score -= t.length * 0.05;

  return { score, indices };
}

/**
 * Convenience: numeric score only, -Infinity for no match.
 */
export function fuzzyScore(query, text) {
  const m = fuzzyMatch(query, text);
  return m ? m.score : -Infinity;
}

/**
 * Score an item that may expose several searchable strings (label + keywords).
 * Returns the best match across all fields, with indices anchored to `label`
 * (so only the visible label is highlighted). null when nothing matches.
 *
 * @param {string} query
 * @param {{label:string, keywords?:string[]}} item
 */
export function scoreItem(query, item) {
  if (query == null || query === "") return { score: 0, indices: [] };
  const label = item.label || "";
  const labelMatch = fuzzyMatch(query, label);
  let best = labelMatch;

  const keywords = item.keywords || [];
  for (const kw of keywords) {
    const m = fuzzyMatch(query, kw);
    if (m && (!best || m.score > best.score)) {
      // Keyword matched better than the label — keep the score but drop indices
      // (we only ever highlight the visible label, never hidden keywords).
      best = { score: m.score - 3, indices: labelMatch ? labelMatch.indices : [] };
    }
  }
  return best; // null when neither label nor any keyword matched
}
